import { FC, KeyboardEvent, useRef } from "react";
import { useClickOutside } from "shared/model";

import { Calendar } from "../calendar";
import formStyles from "../form/form.module.css";
import { ChevronIcon } from "../icons";
import { formatDisplayDateTime } from "./dateTimeValue";
import { TimeFields } from "./TimeFields";
import { useDateTimePicker } from "./useDateTimePicker";
import styles from "./DateTimePicker.module.css";

interface Props {
  // Формат как у datetime-local: "YYYY-MM-DDTHH:mm".
  value: string;
  onChange: (value: string) => void;
  // Дни раньше min в календаре недоступны.
  min?: string;
  placeholder?: string;
  error?: string | null;
  disabled?: boolean;
}

// Свой выбор даты и времени вместо datetime-local: нативный
// календарь не стилизуется и в каждом браузере выглядит по-своему.
export const DateTimePicker: FC<Props> = ({
  value,
  onChange,
  min,
  placeholder = "Select date and time",
  error,
  disabled,
}) => {
  const picker = useDateTimePicker({ value, min, onChange });
  const { isOpen, date } = picker;
  const rootRef = useRef<HTMLDivElement>(null);

  useClickOutside(rootRef, picker.close, isOpen);

  // Escape внутри открытого списка часов закрывает только его.
  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key !== "Escape" || !isOpen || event.defaultPrevented) return;
    event.preventDefault();
    picker.close();
  };

  return (
    <div className={formStyles.field}>
      <div className={styles.root} ref={rootRef} onKeyDown={handleKeyDown}>
        <button
          type="button"
          className={`${formStyles.input} ${styles.trigger} ${error ? formStyles.inputInvalid : ""}`}
          disabled={disabled}
          aria-expanded={isOpen}
          onClick={picker.toggle}
        >
          <span className={date ? "" : styles.placeholder}>
            {date ? formatDisplayDateTime(date) : placeholder}
          </span>
          <ChevronIcon
            direction={isOpen ? "up" : "down"}
            fill="currentColor"
            width={10}
            height={5}
          />
        </button>

        {isOpen && (
          <div className={styles.popup}>
            <Calendar
              viewDate={picker.viewDate}
              rangeStart={date}
              minDate={picker.minDate}
              onPrevMonth={() => picker.shiftMonth(-1)}
              onNextMonth={() => picker.shiftMonth(1)}
              onSelectDay={picker.selectDay}
            />
            <TimeFields
              hours={picker.hours}
              minutes={picker.minutes}
              onChange={picker.setTime}
            />
          </div>
        )}
      </div>
      {error && <span className={formStyles.error}>{error}</span>}
    </div>
  );
};
